import { type Card, type Rank, RANKS } from './game'

export interface PatternMatch {
  pattern: string
  score: number
  positions: number[]
}

export const PATTERNS = {
  fourOfAKind:   { name: 'Four of a kind',   score: 250 },
  fullHouse:     { name: 'Full house',       score: 220 },
  royalFlush:    { name: 'Royal flush',      score: 200 },
  straightFlush: { name: 'Straight flush',   score: 180 },
  acesHigh:      { name: 'Aces High',        score: 150 },
  wheelFlush:    { name: 'Wheel flush',      score: 120 },
  straight:      { name: 'Straight',         score: 100 },
  wheel:         { name: 'Wheel',            score:  80 },
  flush:         { name: 'Flush',            score:  50 },
  twoPair:       { name: 'Two pair',         score:  40 },
  threeOfAKind:  { name: 'Three of a kind',  score:  30 },
  pair:          { name: 'Pair',             score:  20 },
} as const

type PatternDef = (typeof PATTERNS)[keyof typeof PATTERNS]

function match(p: PatternDef, positions: number[]): PatternMatch {
  return { pattern: p.name, score: p.score, positions }
}

export interface ScoringResult {
  rows: (PatternMatch | null)[]
  cols: (PatternMatch | null)[]
  totalScore: number
  highlights: Map<string, 'row' | 'col' | 'both'>
  doubledRows: Set<number>
  doubledCols: Set<number>
}

function rankValue(rank: Rank): number {
  return RANKS.indexOf(rank)
}

function isFlush(slots: (Card | null)[]): boolean {
  const cards = slots.filter((c): c is Card => c !== null)
  return cards.length === 5 && cards.every(c => c.suit === cards[0].suit)
}

type StraightKind = 'wheel' | 'middle' | 'ace-high' | null

function straightKind(slots: (Card | null)[]): StraightKind {
  const cards = slots.filter((c): c is Card => c !== null)
  if (cards.length !== 5) return null
  const ranks = new Set(cards.map(c => c.rank))
  if (ranks.size !== 5) return null

  if (ranks.has('ace') && ranks.has('2') && ranks.has('3') && ranks.has('4') && ranks.has('5')) {
    return 'wheel'
  }
  if (ranks.has('10') && ranks.has('jack') && ranks.has('queen') && ranks.has('king') && ranks.has('ace')) {
    return 'ace-high'
  }
  const values = cards.map(c => rankValue(c.rank)).sort((a, b) => a - b)
  for (let i = 1; i < values.length; i++) {
    if (values[i] !== values[i - 1] + 1) return null
  }
  return 'middle'
}

function findConsecutiveRuns(slots: (Card | null)[]): { rank: Rank; positions: number[] }[] {
  const runs: { rank: Rank; positions: number[] }[] = []
  let i = 0
  while (i < slots.length) {
    const card = slots[i]
    if (card === null) { i++; continue }
    const rank = card.rank
    const start = i
    while (i < slots.length && slots[i] !== null && slots[i]!.rank === rank) i++
    if (i - start >= 2) {
      const positions = Array.from({ length: i - start }, (_, j) => start + j)
      runs.push({ rank, positions })
    }
  }
  return runs
}

function positionsByRank(slots: (Card | null)[]): Map<Rank, number[]> {
  const byRank = new Map<Rank, number[]>()
  for (let i = 0; i < slots.length; i++) {
    const card = slots[i]
    if (card === null) continue
    const existing = byRank.get(card.rank)
    if (existing) existing.push(i)
    else byRank.set(card.rank, [i])
  }
  return byRank
}

export function findPattern(slots: (Card | null)[]): PatternMatch | null {
  if (slots.length !== 5) return null

  const filledCount = slots.filter(c => c !== null).length
  if (filledCount < 2) return null

  const allPositions = [0, 1, 2, 3, 4]

  const byRank = positionsByRank(slots)

  for (const positions of byRank.values()) {
    if (positions.length >= 4) {
      return match(PATTERNS.fourOfAKind, positions.slice(0, 4))
    }
  }

  if (filledCount === 5) {
    let triplePositions: number[] | null = null
    let pairPositions: number[] | null = null
    for (const positions of byRank.values()) {
      if (positions.length === 3) triplePositions = positions
      else if (positions.length === 2) pairPositions = positions
    }
    if (triplePositions && pairPositions) {
      return match(PATTERNS.fullHouse, [...triplePositions, ...pairPositions])
    }

    const kind = straightKind(slots)
    const flush = isFlush(slots)

    if (flush && kind === 'ace-high') return match(PATTERNS.royalFlush, allPositions)
    if (flush && kind === 'middle')   return match(PATTERNS.straightFlush, allPositions)
    if (!flush && kind === 'ace-high') return match(PATTERNS.acesHigh, allPositions)
    if (flush && kind === 'wheel')    return match(PATTERNS.wheelFlush, allPositions)
    if (!flush && kind === 'middle')  return match(PATTERNS.straight, allPositions)
    if (!flush && kind === 'wheel')   return match(PATTERNS.wheel, allPositions)
    if (flush)                        return match(PATTERNS.flush, allPositions)
  }

  const runs = findConsecutiveRuns(slots)

  const run3 = runs.find(r => r.positions.length >= 3)
  if (run3) {
    return match(PATTERNS.threeOfAKind, run3.positions.slice(0, 3))
  }

  const pairs = runs.filter(r => r.positions.length >= 2)
  if (pairs.length >= 2) {
    return match(PATTERNS.twoPair, [...pairs[0].positions.slice(0, 2), ...pairs[1].positions.slice(0, 2)])
  }

  if (pairs.length === 1) {
    return match(PATTERNS.pair, pairs[0].positions.slice(0, 2))
  }

  return null
}

function patternable(card: Card | null): Card | null {
  return card?.blokker?.stage === 3 ? null : card
}

function scoreLine(slots: (Card | null)[]): PatternMatch | null {
  const visible = slots.map(patternable)
  return findPattern(visible)
}

export function scoreGrid(grid: (Card | null)[][]): ScoringResult {
  const rows: (PatternMatch | null)[] = []
  const cols: (PatternMatch | null)[] = []

  for (let r = 0; r < 5; r++) {
    rows.push(scoreLine(grid[r]))
  }

  for (let c = 0; c < 5; c++) {
    const col: (Card | null)[] = grid.map(row => row[c])
    cols.push(scoreLine(col))
  }

  const rowCells = new Set<string>()
  const colCells = new Set<string>()

  rows.forEach((match, r) => {
    if (!match) return
    for (const c of match.positions) {
      rowCells.add(`${r},${c}`)
    }
  })

  cols.forEach((match, c) => {
    if (!match) return
    for (const r of match.positions) {
      colCells.add(`${r},${c}`)
    }
  })

  const highlights = new Map<string, 'row' | 'col' | 'both'>()
  for (const key of rowCells) {
    highlights.set(key, colCells.has(key) ? 'both' : 'row')
  }
  for (const key of colCells) {
    if (!highlights.has(key)) {
      highlights.set(key, 'col')
    }
  }

  const doubledRows = new Set<number>()
  const doubledCols = new Set<number>()
  for (const key of highlights.keys()) {
    if (highlights.get(key) === 'both') {
      const [r, c] = key.split(',').map(Number)
      doubledRows.add(r)
      doubledCols.add(c)
    }
  }

  let totalScore = 0
  rows.forEach((match, r) => {
    if (!match) return
    totalScore += doubledRows.has(r) ? match.score * 2 : match.score
  })
  cols.forEach((match, c) => {
    if (!match) return
    totalScore += doubledCols.has(c) ? match.score * 2 : match.score
  })

  return { rows, cols, totalScore, highlights, doubledRows, doubledCols }
}
